/**
 * Couche service « Demandes d'ouverture de compte ».
 *
 * Un visiteur dépose une demande depuis la page publique et la suit grâce à sa référence ; seul le
 * super-administrateur la valide (l'école et son administrateur sont alors créés) ou la refuse.
 */
import { http, orUndefined } from "./http";
import { toSchoolModel, type CreatedAdmin } from "./platformService";
import type {
  AccountRequest,
  AccountRequestStatus,
  AccountRequestTracking,
  CycleCode,
  School,
  SchoolPlan,
} from "@/types";

interface ApiAccountRequest {
  id: number;
  reference: string;
  schoolName: string;
  shortName: string | null;
  code: string | null;
  city: string;
  country: string | null;
  cycles: CycleCode[];
  plan: SchoolPlan;
  contactName: string;
  contactRole: string | null;
  email: string;
  phone: string;
  studentsEstimate: string | null;
  message: string | null;
  status: AccountRequestStatus;
  rejectionReason: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
  createdSchoolId: number | null;
  createdAt: string;
}

interface ApiTracking {
  reference: string;
  schoolName: string;
  status: AccountRequestStatus;
  createdAt: string;
  reviewedAt: string | null;
  rejectionReason: string | null;
}

function toRequest(r: ApiAccountRequest): AccountRequest {
  return {
    id: String(r.id),
    reference: r.reference,
    schoolName: r.schoolName,
    shortName: r.shortName ?? "",
    code: r.code ?? "",
    city: r.city,
    country: r.country ?? "",
    cycles: r.cycles,
    plan: r.plan ?? "ETABLISSEMENT",
    contactName: r.contactName,
    contactRole: r.contactRole ?? "",
    email: r.email,
    phone: r.phone,
    studentsEstimate: r.studentsEstimate ?? "",
    message: r.message ?? "",
    status: r.status,
    rejectionReason: r.rejectionReason ?? undefined,
    reviewedAt: r.reviewedAt ?? undefined,
    reviewedBy: r.reviewedBy ?? undefined,
    createdSchoolId: r.createdSchoolId === null ? undefined : String(r.createdSchoolId),
    createdAt: r.createdAt,
  };
}

function toTracking(t: ApiTracking): AccountRequestTracking {
  return {
    reference: t.reference,
    schoolName: t.schoolName,
    status: t.status,
    createdAt: t.createdAt,
    reviewedAt: t.reviewedAt ?? undefined,
    rejectionReason: t.rejectionReason ?? undefined,
  };
}

export interface AccountRequestDto {
  schoolName: string;
  shortName: string;
  code: string;
  city: string;
  country: string;
  cycles: CycleCode[];
  plan: SchoolPlan;
  contactName: string;
  contactRole: string;
  email: string;
  phone: string;
  studentsEstimate: string;
  message: string;
  /** Champ piège : invisible pour un humain, rempli par les robots. */
  website?: string;
}

/** Dépose une demande (public). Retourne la référence de suivi. */
export async function createAccountRequest(dto: AccountRequestDto): Promise<AccountRequestTracking> {
  return toTracking(
    await http.post<ApiTracking>(
      "/account-requests",
      {
        schoolName: dto.schoolName.trim(),
        shortName: orUndefined(dto.shortName),
        code: orUndefined(dto.code)?.toUpperCase(),
        city: dto.city.trim(),
        country: orUndefined(dto.country),
        cycles: dto.cycles,
        plan: dto.plan,
        contactName: dto.contactName.trim(),
        contactRole: orUndefined(dto.contactRole),
        email: dto.email.trim(),
        phone: dto.phone.trim(),
        studentsEstimate: orUndefined(dto.studentsEstimate),
        message: orUndefined(dto.message),
        website: orUndefined(dto.website),
      },
      { auth: false },
    ),
  );
}

/** Suivi public d'une demande par sa référence (null si elle est inconnue). */
export async function findAccountRequest(reference: string): Promise<AccountRequestTracking | null> {
  const ref = reference.trim().toUpperCase();
  if (!ref) return null;
  try {
    return toTracking(
      await http.get<ApiTracking>(`/account-requests/track/${encodeURIComponent(ref)}`),
    );
  } catch (err) {
    if ((err as { status?: number }).status === 404) return null;
    throw err;
  }
}

/** Liste des demandes (super-administrateur), les plus récentes en premier. */
export async function listAccountRequests(status?: AccountRequestStatus): Promise<AccountRequest[]> {
  return (await http.get<ApiAccountRequest[]>("/platform/account-requests", { status })).map(
    toRequest,
  );
}

/** Valide une demande : l'école et son administrateur (mot de passe provisoire) sont créés. */
export async function approveAccountRequest(
  id: string,
  code?: string,
  subscription?: { plan: SchoolPlan; subscriptionEndsAt: string },
): Promise<{ request: AccountRequest; school: School; admin: CreatedAdmin }> {
  const res = await http.post<{ request: ApiAccountRequest; school: unknown; admin: CreatedAdmin }>(
    `/platform/account-requests/${id}/approve`,
    {
      code: orUndefined(code)?.toUpperCase(),
      plan: subscription?.plan,
      subscriptionEndsAt: orUndefined(subscription?.subscriptionEndsAt),
    },
  );
  return { request: toRequest(res.request), school: toSchoolModel(res.school), admin: res.admin };
}

/** Refuse une demande ; le motif est visible du demandeur lors du suivi. */
export async function rejectAccountRequest(id: string, reason: string): Promise<AccountRequest> {
  return toRequest(
    await http.post<ApiAccountRequest>(`/platform/account-requests/${id}/reject`, {
      reason: reason.trim(),
    }),
  );
}
